import { BlockId } from '../world/BlockType.js';
import { ItemId, getItemName, getItemColor } from '../items/ItemRegistry.js';
import { colorDe, describir } from '../items/Rareza.js';
import { isTool, getToolDurabilityPercent, getToolDurabilityColor } from '../items/ToolSystem.js';

// ============================================================
// Non-block item colors (negative IDs) for hotbar rendering
// ============================================================
const ITEM_COLORS_MAP = {
  [ItemId.STICK]:          '#B89050',
  [ItemId.CRAFTING_TABLE]: '#8B6B3E',
  [ItemId.FURNACE]:        '#7A7A7A',
  [ItemId.RAW_IRON]:       '#C4A06A',
  [ItemId.RAW_GOLD]:       '#E8D44A',
  [ItemId.DIAMOND_GEM]:    '#5AE8D4',
  [ItemId.COAL]:           '#333333',
  [ItemId.REDSTONE_DUST]:  '#DD2222',
  [ItemId.LAPIS]:          '#2244AA',
  [ItemId.EMERALD_GEM]:    '#22CC44',
  [ItemId.FLINT]:          '#555555',
  [ItemId.STRING]:         '#E0E0E0',
  [ItemId.BONE]:           '#E8E8D8',
  [ItemId.ARROW_ITEM]:     '#8B6B3E',
  [ItemId.GUNPOWDER]:      '#666666',
  [ItemId.ROTTEN_FLESH]:   '#8B5A2B',
  [ItemId.SPIDER_EYE]:     '#993333',
  [ItemId.FEATHER]:        '#F0F0F0',
  [ItemId.LEATHER]:        '#A0622A',
  [ItemId.RAW_BEEF]:       '#CC4444',
  [ItemId.COOKED_BEEF]:    '#884422',
  [ItemId.RAW_PORK]:       '#DDAAAA',
  [ItemId.COOKED_PORK]:    '#AA6633',
  [ItemId.RAW_CHICKEN]:    '#EEBBAA',
  [ItemId.COOKED_CHICKEN]: '#BB7744',
  [ItemId.BREAD]:          '#C49840',
  [ItemId.APPLE]:          '#DD2222',
  [ItemId.IRON_INGOT]:     '#D8D8D8',
  [ItemId.GOLD_INGOT]:     '#FFD700',
  [ItemId.WHEAT_ITEM]:     '#D4AA30',
  [ItemId.SEEDS]:          '#6A8A20',
  [ItemId.BONE_MEAL]:      '#E8E8D0',
  [ItemId.BOW]:            '#6B4226',
  [ItemId.SLIME_BALL]:     '#66CC44',
  [ItemId.ENDER_PEARL]:    '#116655',

  [ItemId.POTION_SPEED]:           '#7CAFC6',
  [ItemId.POTION_STRENGTH]:        '#932423',
  [ItemId.POTION_HEALING]:         '#F82423',
  [ItemId.POTION_FIRE_RESISTANCE]: '#E49A3A',
  [ItemId.POTION_NIGHT_VISION]:    '#1F1FA1',
  [ItemId.POTION_REGENERATION]:    '#CD5CAB',

  [ItemId.GLASS_BOTTLE]:   '#AADDEE',
  [ItemId.NETHER_WART]:    '#8B2020',
  [ItemId.BLAZE_POWDER]:   '#FFCC00',
  [ItemId.AWKWARD_POTION]:  '#6A4A8A',

  [ItemId.GHAST_TEAR]:     '#E8E8FF',
  [ItemId.BLAZE_ROD]:      '#FFB840',
  [ItemId.NETHER_QUARTZ]:  '#F0E8E0',

  [ItemId.IRON_HELMET]:     '#C8C8C8',
  [ItemId.IRON_CHESTPLATE]: '#C8C8C8',
  [ItemId.IRON_LEGGINGS]:   '#C8C8C8',
  [ItemId.IRON_BOOTS]:      '#C8C8C8',
  [ItemId.GOLD_HELMET]:     '#FFD700',
  [ItemId.GOLD_CHESTPLATE]: '#FFD700',
  [ItemId.GOLD_LEGGINGS]:   '#FFD700',
  [ItemId.GOLD_BOOTS]:      '#FFD700',
  [ItemId.DIAMOND_HELMET]:     '#4AEDD9',
  [ItemId.DIAMOND_CHESTPLATE]: '#4AEDD9',
  [ItemId.DIAMOND_LEGGINGS]:   '#4AEDD9',
  [ItemId.DIAMOND_BOOTS]:      '#4AEDD9',

  [ItemId.BUCKET]:          '#AAAAAA',
  [ItemId.PAINTING]:        '#8B5E3C',
  [ItemId.BOOK]:            '#8B4513',
  [ItemId.MINECART]:        '#999999',
  [ItemId.RAILS]:           '#B0B0B0',
  [ItemId.PISTON]:          '#7A7A5A',

  [ItemId.GLOWSTONE_DUST]:  '#DDCC40',
  [ItemId.SNOWBALL]:         '#F0F0FF',
  [ItemId.BRICK_ITEM]:       '#AA5533',

  [ItemId.PAPER]:            '#F5F5DC',
  [ItemId.MAP]:              '#D2B48C',

  // Fishing
  [ItemId.FISHING_ROD]:      '#8B4513',
  [ItemId.RAW_COD]:          '#C4A882',
  [ItemId.COOKED_COD]:       '#D4A862',
  [ItemId.RAW_SALMON]:       '#FF6347',
  [ItemId.COOKED_SALMON]:    '#FF8C69',
  [ItemId.TROPICAL_FISH]:    '#FFD700',
  [ItemId.PUFFERFISH]:       '#FFE4B5',

  // Boss drops
  [ItemId.NETHER_STAR]:      '#FFFFAA',
  [ItemId.WITHER_SKULL]:     '#333333',

  // Farming & Village items (Fase 5)
  [ItemId.CARROT_ITEM]:      '#E87020',
  [ItemId.POTATO_ITEM]:      '#C4A060',
  [ItemId.BEETROOT_ITEM]:    '#8B1A1A',
  [ItemId.BAKED_POTATO]:     '#D4AA40',
  [ItemId.BEETROOT_SOUP]:    '#AA3030',
  [ItemId.SWEET_BERRIES]:    '#DD3333',
  [ItemId.SUGAR]:            '#F0F0F0',
  [ItemId.PUMPKIN_SEEDS]:    '#6A8A20',
  [ItemId.MELON_SEEDS]:      '#5A9A30',
  [ItemId.MELON_SLICE]:      '#6AAA30',
  [ItemId.TRIDENT]:          '#3388BB',
  // Exploration & Adventure items (Fase 7)
  [ItemId.SHIELD]:           '#8B6B3E',
  [ItemId.CROSSBOW]:         '#6B4226',
  [ItemId.HEART_OF_SEA]:     '#1A8AAA',
  [ItemId.TREASURE_MAP]:     '#C4A060',
};

// Non-block item names (Spanish) for hotbar tooltip
const ITEM_NAMES_MAP = {
  [ItemId.STICK]:          'Palo',
  [ItemId.CRAFTING_TABLE]: 'Mesa de Crafteo',
  [ItemId.FURNACE]:        'Horno',
  [ItemId.RAW_IRON]:       'Hierro Crudo',
  [ItemId.RAW_GOLD]:       'Oro Crudo',
  [ItemId.DIAMOND_GEM]:    'Diamante',
  [ItemId.COAL]:           'Carbón',
  [ItemId.REDSTONE_DUST]:  'Polvo de Redstone',
  [ItemId.LAPIS]:          'Lapislázuli',
  [ItemId.EMERALD_GEM]:    'Esmeralda',
  [ItemId.FLINT]:          'Pedernal',
  [ItemId.STRING]:         'Hilo',
  [ItemId.BONE]:           'Hueso',
  [ItemId.ARROW_ITEM]:     'Flecha',
  [ItemId.GUNPOWDER]:      'Pólvora',
  [ItemId.ROTTEN_FLESH]:   'Carne Podrida',
  [ItemId.SPIDER_EYE]:     'Ojo de Araña',
  [ItemId.FEATHER]:        'Pluma',
  [ItemId.LEATHER]:        'Cuero',
  [ItemId.RAW_BEEF]:       'Carne Cruda',
  [ItemId.COOKED_BEEF]:    'Filete',
  [ItemId.RAW_PORK]:       'Cerdo Crudo',
  [ItemId.COOKED_PORK]:    'Cerdo Cocido',
  [ItemId.RAW_CHICKEN]:    'Pollo Crudo',
  [ItemId.COOKED_CHICKEN]: 'Pollo Cocido',
  [ItemId.BREAD]:          'Pan',
  [ItemId.APPLE]:          'Manzana',
  [ItemId.IRON_INGOT]:     'Lingote de Hierro',
  [ItemId.GOLD_INGOT]:     'Lingote de Oro',
  [ItemId.WHEAT_ITEM]:     'Trigo',
  [ItemId.SEEDS]:          'Semillas',
  [ItemId.BONE_MEAL]:      'Harina de Hueso',
  [ItemId.BOW]:            'Arco',
  [ItemId.SLIME_BALL]:     'Bola de Slime',
  [ItemId.ENDER_PEARL]:    'Perla de Ender',

  [ItemId.POTION_SPEED]:           'Poción de Velocidad',
  [ItemId.POTION_STRENGTH]:        'Poción de Fuerza',
  [ItemId.POTION_HEALING]:         'Poción de Curación',
  [ItemId.POTION_FIRE_RESISTANCE]: 'Poción de Resist. Fuego',
  [ItemId.POTION_NIGHT_VISION]:    'Poción de Visión Nocturna',
  [ItemId.POTION_REGENERATION]:    'Poción de Regeneración',

  [ItemId.GLASS_BOTTLE]:   'Botella de Vidrio',
  [ItemId.NETHER_WART]:    'Verruga del Nether',
  [ItemId.BLAZE_POWDER]:   'Polvo de Blaze',
  [ItemId.AWKWARD_POTION]:  'Poción Rara',

  [ItemId.GHAST_TEAR]:     'Lágrima de Ghast',
  [ItemId.BLAZE_ROD]:      'Vara de Blaze',
  [ItemId.NETHER_QUARTZ]:  'Cuarzo del Nether',

  [ItemId.IRON_HELMET]:     'Casco de Hierro',
  [ItemId.IRON_CHESTPLATE]: 'Pechera de Hierro',
  [ItemId.IRON_LEGGINGS]:   'Pantalones de Hierro',
  [ItemId.IRON_BOOTS]:      'Botas de Hierro',
  [ItemId.GOLD_HELMET]:     'Casco de Oro',
  [ItemId.GOLD_CHESTPLATE]: 'Pechera de Oro',
  [ItemId.GOLD_LEGGINGS]:   'Pantalones de Oro',
  [ItemId.GOLD_BOOTS]:      'Botas de Oro',
  [ItemId.DIAMOND_HELMET]:     'Casco de Diamante',
  [ItemId.DIAMOND_CHESTPLATE]: 'Pechera de Diamante',
  [ItemId.DIAMOND_LEGGINGS]:   'Pantalones de Diamante',
  [ItemId.DIAMOND_BOOTS]:      'Botas de Diamante',

  [ItemId.BUCKET]:          'Balde',
  [ItemId.PAINTING]:        'Cuadro',
  [ItemId.BOOK]:            'Libro',
  [ItemId.MINECART]:        'Vagoneta',
  [ItemId.RAILS]:           'Rieles',
  [ItemId.PISTON]:          'Pistón',

  [ItemId.GLOWSTONE_DUST]:  'Polvo de Piedra Luminosa',
  [ItemId.SNOWBALL]:         'Bola de Nieve',
  [ItemId.BRICK_ITEM]:       'Ladrillo',

  [ItemId.PAPER]:            'Papel',
  [ItemId.MAP]:              'Mapa',

  // Fishing
  [ItemId.FISHING_ROD]:      'Caña de Pescar',
  [ItemId.RAW_COD]:          'Bacalao Crudo',
  [ItemId.COOKED_COD]:       'Bacalao Cocido',
  [ItemId.RAW_SALMON]:       'Salmón Crudo',
  [ItemId.COOKED_SALMON]:    'Salmón Cocido',
  [ItemId.TROPICAL_FISH]:    'Pez Tropical',
  [ItemId.PUFFERFISH]:       'Pez Globo',

  // Boss drops
  [ItemId.NETHER_STAR]:      'Estrella del Nether',
  [ItemId.WITHER_SKULL]:     'Cráneo de Wither',

  // Farming & Village items (Fase 5)
  [ItemId.CARROT_ITEM]:      'Zanahoria',
  [ItemId.POTATO_ITEM]:      'Papa',
  [ItemId.BEETROOT_ITEM]:    'Remolacha',
  [ItemId.BAKED_POTATO]:     'Papa al Horno',
  [ItemId.BEETROOT_SOUP]:    'Sopa de Remolacha',
  [ItemId.SWEET_BERRIES]:    'Bayas Dulces',
  [ItemId.SUGAR]:            'Azúcar',
  [ItemId.PUMPKIN_SEEDS]:    'Semillas de Calabaza',
  [ItemId.MELON_SEEDS]:      'Semillas de Melón',
  [ItemId.MELON_SLICE]:      'Rebanada de Melón',
  [ItemId.TRIDENT]:          'Tridente',
  // Exploration & Adventure items (Fase 7)
  [ItemId.SHIELD]:           'Escudo',
  [ItemId.CROSSBOW]:         'Ballesta',
  [ItemId.HEART_OF_SEA]:     'Corazón del Mar',
  [ItemId.TREASURE_MAP]:     'Mapa del Tesoro',
};

const BLOCK_COLORS = {
  [BlockId.GRASS]: '#5B8C2A',
  [BlockId.DIRT]: '#8B6B3E',
  [BlockId.STONE]: '#8A8A8A',
  [BlockId.SAND]: '#D4C483',
  [BlockId.WOOD]: '#6B4F2E',
  [BlockId.LEAVES]: '#3A7A1A',
  [BlockId.WATER]: '#3366AA',
  [BlockId.BEDROCK]: '#3A3A3A',
  [BlockId.SNOW_GRASS]: '#DDDDDD',
  [BlockId.SANDSTONE]: '#C4A050',
  [BlockId.CACTUS]: '#2A6A10',
  [BlockId.PLANKS]: '#B89050',
  [BlockId.COBBLESTONE]: '#7A7A7A',
  [BlockId.GLASS]: '#AADDFF',
  [BlockId.WHEAT]: '#D4AA30',
  [BlockId.DOOR_CLOSED]: '#8B5E3C',
  [BlockId.DOOR_OPEN]: '#8B5E3C',
  [BlockId.COAL_ORE]: '#6A6A6A',
  [BlockId.IRON_ORE]: '#8A8A7A',
  [BlockId.GOLD_ORE]: '#8A8A5A',
  [BlockId.DIAMOND_ORE]: '#7A9A9A',
  [BlockId.REDSTONE_ORE]: '#8A6A6A',
  [BlockId.LAPIS_ORE]: '#6A6A8A',
  [BlockId.EMERALD_ORE]: '#6A8A6A',
  [BlockId.OBSIDIAN]: '#1A0A2E',
  [BlockId.TORCH]: '#FF8800',
  [BlockId.CRAFTING_TABLE]: '#B89050',
  [BlockId.FURNACE]: '#7A7A7A',
  [BlockId.FURNACE_LIT]: '#8A6A5A',
  [BlockId.CHEST]: '#9C7C4E',
  [BlockId.TNT]: '#CC2200',
  [BlockId.BOOKSHELF]: '#8B6B3E',
  [BlockId.BRICKS]: '#AA5533',
  [BlockId.IRON_BLOCK]: '#D8D8D8',
  [BlockId.GOLD_BLOCK]: '#E8C840',
  [BlockId.DIAMOND_BLOCK]: '#5AE8D4',
  [BlockId.SLAB_STONE]: '#8A8A8A',
  [BlockId.SLAB_WOOD]: '#B89050',
  [BlockId.FENCE]: '#B89050',
  [BlockId.LADDER]: '#8B6B3E',
  [BlockId.FLOWER_RED]: '#DD2222',
  [BlockId.FLOWER_YELLOW]: '#FFDD00',
  [BlockId.TALL_GRASS]: '#4A9A30',
  [BlockId.MUSHROOM_RED]: '#DD2222',
  [BlockId.MUSHROOM_BROWN]: '#8B6B3E',
  [BlockId.GRAVEL]: '#808080',
  [BlockId.CLAY]: '#A0A0AA',
  [BlockId.LOG_BIRCH]: '#E8E0D0',
  [BlockId.LEAVES_BIRCH]: '#5AAA30',
  [BlockId.SNOW]: '#F4F4F8',
  [BlockId.ICE]: '#8CC8FF',
  [BlockId.PUMPKIN]: '#DD8820',
  [BlockId.MELON]: '#8AAA30',
  [BlockId.WOOL_WHITE]: '#F0F0F0',
  [BlockId.MOSS_STONE]: '#6A7A6A',
  [BlockId.NETHERRACK]: '#6A2020',
  [BlockId.GLOWSTONE]: '#D4AA40',
  [BlockId.SOUL_SAND]: '#5A4830',
  [BlockId.NETHER_BRICKS]: '#3A1A2A',
  [BlockId.END_STONE]: '#DDDDA8',
  [BlockId.BED]: '#AA2222',
  [BlockId.FARMLAND]: '#8B6B3E',
  [BlockId.FARMLAND_WET]: '#5A4020',
  [BlockId.JUNGLE_LOG]: '#4A3018',
  [BlockId.JUNGLE_LEAVES]: '#2A5A10',
  [BlockId.ACACIA_LOG]: '#8A7A60',
  [BlockId.ACACIA_LEAVES]: '#CC6A20',
  [BlockId.VINE]: '#2A6A10',
  [BlockId.LILY_PAD]: '#2A7A10',
  [BlockId.ENCHANTING_TABLE]: '#1A0A2E',
  [BlockId.BREWING_STAND]:    '#7A7A7A',
  [BlockId.NETHER_PORTAL]:    '#7722CC',
  [BlockId.LAVA]:             '#CC4400',
  [BlockId.NETHER_GOLD_ORE]:  '#7A2828',
  [BlockId.NETHER_QUARTZ_ORE]:'#7A2828',
  [BlockId.REDSTONE_WIRE]:    '#CC1111',
  [BlockId.LEVER]:            '#7A7A7A',
  [BlockId.BUTTON]:           '#9A9A9A',
  [BlockId.PRESSURE_PLATE]:   '#8A8A8A',
  [BlockId.REDSTONE_TORCH]:   '#DD1111',
  [BlockId.REPEATER]:         '#8A7A7A',
  [BlockId.TRAPDOOR]:         '#8B6B3E',
  [BlockId.STAIRS_STONE]:     '#8A8A8A',
  [BlockId.STAIRS_WOOD]:      '#B89050',
  [BlockId.SIGN]:             '#B89050',
  [BlockId.JUKEBOX]:          '#6B4020',
  [BlockId.NOTEBLOCK]:        '#B89050',
  [BlockId.GRAVE_BASE]:       '#3D2B1A',
  [BlockId.COFFIN]:           '#3B2316',
  [BlockId.COFFIN_OPEN]:      '#8B1A1A',
  [BlockId.CROSS]:            '#6B4226',
  [BlockId.END_PORTAL_FRAME]: '#2E4032',
  [BlockId.END_PORTAL]:       '#0A0012',
  [BlockId.PURPUR_BLOCK]:     '#9B59B6',
  [BlockId.PURPUR_PILLAR]:    '#9B59B6',
  [BlockId.END_STONE_BRICKS]: '#D4C99E',
  [BlockId.CHORUS_PLANT]:     '#7D3C98',
  [BlockId.DRAGON_EGG]:       '#1A0A2E',
  [BlockId.END_ROD]:          '#E8D8F0',
  // Farming & Village blocks (Fase 5)
  [BlockId.CARROT]:           '#6B4F2E',
  [BlockId.POTATO]:           '#6B4F2E',
  [BlockId.BEETROOT]:         '#6B4F2E',
  [BlockId.BERRY_BUSH]:       '#2A6A10',
  [BlockId.PUMPKIN_STEM]:     '#3A8A20',
  [BlockId.MELON_STEM]:       '#4A9A30',
  [BlockId.SUGAR_CANE]:       '#5AAA40',
  [BlockId.BELL]:             '#FFD700',
  // Redstone automation (Fase 6)
  [BlockId.PISTON]:           '#8A8A6A',
  [BlockId.PISTON_HEAD]:      '#C8A060',
  [BlockId.STICKY_PISTON]:    '#6A9A5A',
  [BlockId.HOPPER]:           '#444444',
  [BlockId.DROPPER]:          '#7A7A7A',
  [BlockId.DISPENSER]:        '#7A7A7A',
  [BlockId.OBSERVER]:         '#8A8A8A',
  [BlockId.REDSTONE_LAMP]:    '#8A6A3A',
  [BlockId.REDSTONE_LAMP_LIT]:'#EEDD60',
  [BlockId.DAYLIGHT_SENSOR]:  '#6688BB',
  [BlockId.POWERED_RAIL]:     '#FFD700',
  [BlockId.DETECTOR_RAIL]:    '#B0B0B0',
  [BlockId.RAIL]:             '#B0B0B0',
  // Exploration blocks (Fase 7)
  [BlockId.DRIPSTONE]:        '#B8A88A',
  [BlockId.POINTED_DRIPSTONE]:'#A89070',
  [BlockId.AZALEA]:           '#4A8A30',
  [BlockId.AZALEA_LEAVES]:    '#6BAA45',
  [BlockId.GLOW_BERRY_VINE]:  '#2A5A18',
  [BlockId.SCULK]:            '#0C4A4E',
  [BlockId.SCULK_SENSOR]:     '#0E5A5E',
  [BlockId.MOSS_BLOCK]:       '#3A7A20',
  [BlockId.MYCELIUM]:         '#8A7A8A',
  [BlockId.TERRACOTTA]:       '#9A6B48',
  [BlockId.TERRACOTTA_RED]:   '#9A4A3A',
  [BlockId.TERRACOTTA_ORANGE]:'#B87040',
  // Decorative blocks (Fase 8)
  [BlockId.WOOL_RED]:          '#B02020',
  [BlockId.WOOL_BLUE]:         '#2040B0',
  [BlockId.WOOL_GREEN]:        '#30A030',
  [BlockId.WOOL_YELLOW]:       '#D4C020',
  [BlockId.WOOL_BLACK]:        '#1A1A1A',
  [BlockId.WOOL_ORANGE]:       '#D07020',
  [BlockId.WOOL_PURPLE]:       '#7030A0',
  [BlockId.GLASS_RED]:         '#CC4444',
  [BlockId.GLASS_BLUE]:        '#4466CC',
  [BlockId.GLASS_GREEN]:       '#44AA44',
  [BlockId.GLASS_YELLOW]:      '#CCBB44',
  [BlockId.LANTERN]:           '#FFD050',
  [BlockId.CHAIN]:             '#6A6A6A',
  [BlockId.CAMPFIRE]:          '#5A4030',
  [BlockId.CAMPFIRE_LIT]:      '#FF6600',
  [BlockId.CANDLE]:            '#E8D8A0',
  [BlockId.SCAFFOLDING]:       '#C4A040',
  [BlockId.BARREL]:            '#8B6B3E',
  [BlockId.LOOM]:              '#8B6B3E',
  [BlockId.SMOKER]:            '#6A6A6A',
};

// Minecraft-style block top/side colors for 3D block icons
const BLOCK_TOP_COLORS = {
  [BlockId.GRASS]: '#7EC850',
  [BlockId.DIRT]: '#8B6B3E',
  [BlockId.STONE]: '#9A9A9A',
  [BlockId.SAND]: '#E4D493',
  [BlockId.WOOD]: '#9B7F4E',
  [BlockId.LEAVES]: '#4A8A2A',
  [BlockId.WATER]: '#4488CC',
  [BlockId.BEDROCK]: '#4A4A4A',
  [BlockId.SNOW_GRASS]: '#F0F0F0',
  [BlockId.SANDSTONE]: '#D4B060',
  [BlockId.CACTUS]: '#3A8A20',
  [BlockId.PLANKS]: '#C8A060',
  [BlockId.COBBLESTONE]: '#8A8A8A',
  [BlockId.GLASS]: '#CCEEFF',
  [BlockId.WHEAT]: '#E4BA40',
  [BlockId.DOOR_CLOSED]: '#9B6E4C',
  [BlockId.DOOR_OPEN]: '#9B6E4C',
  [BlockId.COAL_ORE]: '#7A7A7A',
  [BlockId.IRON_ORE]: '#9A9A8A',
  [BlockId.GOLD_ORE]: '#9A9A6A',
  [BlockId.DIAMOND_ORE]: '#8AAAAA',
  [BlockId.REDSTONE_ORE]: '#9A7A7A',
  [BlockId.LAPIS_ORE]: '#7A7A9A',
  [BlockId.EMERALD_ORE]: '#7A9A7A',
  [BlockId.OBSIDIAN]: '#2A1A3E',
  [BlockId.TORCH]: '#FFCC00',
  [BlockId.CRAFTING_TABLE]: '#C49860',
  [BlockId.FURNACE]: '#8A8A8A',
  [BlockId.FURNACE_LIT]: '#9A7A6A',
  [BlockId.CHEST]: '#8B6B3E',
  [BlockId.TNT]: '#DD3300',
  [BlockId.BOOKSHELF]: '#B89050',
  [BlockId.BRICKS]: '#BB6644',
  [BlockId.IRON_BLOCK]: '#E4E4E4',
  [BlockId.GOLD_BLOCK]: '#F0D850',
  [BlockId.DIAMOND_BLOCK]: '#6CF0E0',
  [BlockId.SLAB_STONE]: '#9A9A9A',
  [BlockId.SLAB_WOOD]: '#C8A060',
  [BlockId.FENCE]: '#C8A060',
  [BlockId.LADDER]: '#A08040',
  [BlockId.FLOWER_RED]: '#FF3333',
  [BlockId.FLOWER_YELLOW]: '#FFEE44',
  [BlockId.TALL_GRASS]: '#5BAA40',
  [BlockId.MUSHROOM_RED]: '#FF3333',
  [BlockId.MUSHROOM_BROWN]: '#9C7C4E',
  [BlockId.GRAVEL]: '#909090',
  [BlockId.CLAY]: '#B0B0B8',
  [BlockId.LOG_BIRCH]: '#D8D0B8',
  [BlockId.LEAVES_BIRCH]: '#6ABB40',
  [BlockId.SNOW]: '#FFFFFF',
  [BlockId.ICE]: '#A0D8FF',
  [BlockId.PUMPKIN]: '#CC8820',
  [BlockId.MELON]: '#4A8A20',
  [BlockId.WOOL_WHITE]: '#FFFFFF',
  [BlockId.MOSS_STONE]: '#7A8A7A',
  [BlockId.NETHERRACK]: '#7A2828',
  [BlockId.GLOWSTONE]: '#EEDD60',
  [BlockId.SOUL_SAND]: '#6A5840',
  [BlockId.NETHER_BRICKS]: '#4A2A3A',
  [BlockId.END_STONE]: '#EEEEB8',
  [BlockId.BED]: '#CC3333',
  [BlockId.FARMLAND]: '#7A5B2E',
  [BlockId.FARMLAND_WET]: '#4A3015',
  [BlockId.JUNGLE_LOG]: '#5A4028',
  [BlockId.JUNGLE_LEAVES]: '#3A6A1A',
  [BlockId.ACACIA_LOG]: '#9A8A70',
  [BlockId.ACACIA_LEAVES]: '#DD7A30',
  [BlockId.VINE]: '#3A7A1A',
  [BlockId.LILY_PAD]: '#3A8A20',
  [BlockId.ENCHANTING_TABLE]: '#2A1A3E',
  [BlockId.BREWING_STAND]:    '#8A8A8A',
  [BlockId.NETHER_PORTAL]:    '#AA44FF',
  [BlockId.LAVA]:             '#FF6600',
  [BlockId.NETHER_GOLD_ORE]:  '#8A3838',
  [BlockId.NETHER_QUARTZ_ORE]:'#8A3838',
  [BlockId.REDSTONE_WIRE]:    '#DD2222',
  [BlockId.LEVER]:            '#8A8A8A',
  [BlockId.BUTTON]:           '#AAAAAA',
  [BlockId.PRESSURE_PLATE]:   '#9A9A9A',
  [BlockId.REDSTONE_TORCH]:   '#FF2222',
  [BlockId.REPEATER]:         '#9A8A8A',
  [BlockId.TRAPDOOR]:         '#9C7C4E',
  [BlockId.STAIRS_STONE]:     '#9A9A9A',
  [BlockId.STAIRS_WOOD]:      '#C8A060',
  [BlockId.SIGN]:             '#C8A060',
  [BlockId.JUKEBOX]:          '#7B5030',
  [BlockId.NOTEBLOCK]:        '#C8A060',
  [BlockId.GRAVE_BASE]:       '#4A3520',
  [BlockId.COFFIN]:           '#4A2E1C',
  [BlockId.COFFIN_OPEN]:      '#9C2020',
  [BlockId.CROSS]:            '#7A4E2E',
  [BlockId.END_PORTAL_FRAME]: '#3A5040',
  [BlockId.END_PORTAL]:       '#1A0030',
  [BlockId.PURPUR_BLOCK]:     '#A96BC4',
  [BlockId.PURPUR_PILLAR]:    '#B070CC',
  [BlockId.END_STONE_BRICKS]: '#C8BE90',
  [BlockId.CHORUS_PLANT]:     '#9B59B6',
  [BlockId.DRAGON_EGG]:       '#2A1040',
  [BlockId.END_ROD]:          '#F0E8FF',
  // Farming & Village blocks (Fase 5)
  [BlockId.CARROT]:           '#3A8A20',
  [BlockId.POTATO]:           '#3A8A20',
  [BlockId.BEETROOT]:         '#3A8A20',
  [BlockId.BERRY_BUSH]:       '#3A8A20',
  [BlockId.PUMPKIN_STEM]:     '#4AAA30',
  [BlockId.MELON_STEM]:       '#5AAA40',
  [BlockId.SUGAR_CANE]:       '#6ABB50',
  [BlockId.BELL]:             '#FFE84A',
  // Redstone automation (Fase 6)
  [BlockId.PISTON]:           '#9A9A7A',
  [BlockId.PISTON_HEAD]:      '#D4B070',
  [BlockId.STICKY_PISTON]:    '#77DD55',
  [BlockId.HOPPER]:           '#555555',
  [BlockId.DROPPER]:          '#8A8A8A',
  [BlockId.DISPENSER]:        '#8A8A8A',
  [BlockId.OBSERVER]:         '#9A9A9A',
  [BlockId.REDSTONE_LAMP]:    '#9A7A4A',
  [BlockId.REDSTONE_LAMP_LIT]:'#FFEE80',
  [BlockId.DAYLIGHT_SENSOR]:  '#7799CC',
  [BlockId.POWERED_RAIL]:     '#FFE84A',
  [BlockId.DETECTOR_RAIL]:    '#C8C8C8',
  [BlockId.RAIL]:             '#C8C8C8',
  // Exploration blocks (Fase 7)
  [BlockId.DRIPSTONE]:        '#C8B89A',
  [BlockId.POINTED_DRIPSTONE]:'#B8A080',
  [BlockId.AZALEA]:           '#5A9A40',
  [BlockId.AZALEA_LEAVES]:    '#7CBA55',
  [BlockId.GLOW_BERRY_VINE]:  '#3A6A28',
  [BlockId.SCULK]:            '#0E5A5E',
  [BlockId.SCULK_SENSOR]:     '#D4AA30',
  [BlockId.MOSS_BLOCK]:       '#4A8A30',
  [BlockId.MYCELIUM]:         '#9A8A9A',
  [BlockId.TERRACOTTA]:       '#AA7B58',
  [BlockId.TERRACOTTA_RED]:   '#AA5A4A',
  [BlockId.TERRACOTTA_ORANGE]:'#C88050',
  // Decorative blocks (Fase 8)
  [BlockId.WOOL_RED]:          '#C02828',
  [BlockId.WOOL_BLUE]:         '#2848C0',
  [BlockId.WOOL_GREEN]:        '#38B038',
  [BlockId.WOOL_YELLOW]:       '#E4D028',
  [BlockId.WOOL_BLACK]:        '#252525',
  [BlockId.WOOL_ORANGE]:       '#E08028',
  [BlockId.WOOL_PURPLE]:       '#8038B0',
  [BlockId.GLASS_RED]:         '#DD6666',
  [BlockId.GLASS_BLUE]:        '#6688DD',
  [BlockId.GLASS_GREEN]:       '#66CC66',
  [BlockId.GLASS_YELLOW]:      '#DDCC66',
  [BlockId.LANTERN]:           '#FFEE88',
  [BlockId.CHAIN]:             '#888888',
  [BlockId.CAMPFIRE]:          '#6B4226',
  [BlockId.CAMPFIRE_LIT]:      '#FFCC00',
  [BlockId.CANDLE]:            '#FFCC00',
  [BlockId.SCAFFOLDING]:       '#D4B050',
  [BlockId.BARREL]:            '#9C7C4E',
  [BlockId.LOOM]:              '#E8E0D0',
  [BlockId.SMOKER]:            '#7A7A7A',
};

const BLOCK_NAMES = {
  [BlockId.GRASS]: 'Pasto',
  [BlockId.DIRT]: 'Tierra',
  [BlockId.STONE]: 'Piedra',
  [BlockId.SAND]: 'Arena',
  [BlockId.WOOD]: 'Madera',
  [BlockId.LEAVES]: 'Hojas',
  [BlockId.WATER]: 'Agua',
  [BlockId.BEDROCK]: 'Roca Madre',
  [BlockId.SNOW_GRASS]: 'Nieve',
  [BlockId.SANDSTONE]: 'Arenisca',
  [BlockId.CACTUS]: 'Cactus',
  [BlockId.PLANKS]: 'Tablas',
  [BlockId.COBBLESTONE]: 'Adoquín',
  [BlockId.GLASS]: 'Vidrio',
  [BlockId.WHEAT]: 'Trigo',
  [BlockId.DOOR_CLOSED]: 'Puerta',
  [BlockId.DOOR_OPEN]: 'Puerta',
  [BlockId.COAL_ORE]: 'Carbón',
  [BlockId.IRON_ORE]: 'Hierro',
  [BlockId.GOLD_ORE]: 'Oro',
  [BlockId.DIAMOND_ORE]: 'Diamante',
  [BlockId.REDSTONE_ORE]: 'Redstone',
  [BlockId.LAPIS_ORE]: 'Lapislázuli',
  [BlockId.EMERALD_ORE]: 'Esmeralda',
  [BlockId.OBSIDIAN]: 'Obsidiana',
  [BlockId.TORCH]: 'Antorcha',
  [BlockId.CRAFTING_TABLE]: 'Mesa de Crafteo',
  [BlockId.FURNACE]: 'Horno',
  [BlockId.FURNACE_LIT]: 'Horno',
  [BlockId.CHEST]: 'Cofre',
  [BlockId.TNT]: 'TNT',
  [BlockId.BOOKSHELF]: 'Librería',
  [BlockId.BRICKS]: 'Ladrillos',
  [BlockId.IRON_BLOCK]: 'Bloque de Hierro',
  [BlockId.GOLD_BLOCK]: 'Bloque de Oro',
  [BlockId.DIAMOND_BLOCK]: 'Bloque de Diamante',
  [BlockId.SLAB_STONE]: 'Losa de Piedra',
  [BlockId.SLAB_WOOD]: 'Losa de Madera',
  [BlockId.FENCE]: 'Cerca',
  [BlockId.LADDER]: 'Escalera',
  [BlockId.FLOWER_RED]: 'Flor Roja',
  [BlockId.FLOWER_YELLOW]: 'Flor Amarilla',
  [BlockId.TALL_GRASS]: 'Pasto Alto',
  [BlockId.MUSHROOM_RED]: 'Hongo Rojo',
  [BlockId.MUSHROOM_BROWN]: 'Hongo Marrón',
  [BlockId.GRAVEL]: 'Grava',
  [BlockId.CLAY]: 'Arcilla',
  [BlockId.LOG_BIRCH]: 'Abedul',
  [BlockId.LEAVES_BIRCH]: 'Hojas de Abedul',
  [BlockId.SNOW]: 'Nieve',
  [BlockId.ICE]: 'Hielo',
  [BlockId.PUMPKIN]: 'Calabaza',
  [BlockId.MELON]: 'Sandía',
  [BlockId.WOOL_WHITE]: 'Lana',
  [BlockId.MOSS_STONE]: 'Piedra Musgosa',
  [BlockId.NETHERRACK]: 'Netherrack',
  [BlockId.GLOWSTONE]: 'Piedra Luminosa',
  [BlockId.SOUL_SAND]: 'Arena de Almas',
  [BlockId.NETHER_BRICKS]: 'Ladrillos del Nether',
  [BlockId.END_STONE]: 'Piedra del End',
  [BlockId.BED]: 'Cama',
  [BlockId.FARMLAND]: 'Tierra Arada',
  [BlockId.FARMLAND_WET]: 'Tierra Húmeda',
  [BlockId.JUNGLE_LOG]: 'Tronco de Jungla',
  [BlockId.JUNGLE_LEAVES]: 'Hojas de Jungla',
  [BlockId.ACACIA_LOG]: 'Tronco de Acacia',
  [BlockId.ACACIA_LEAVES]: 'Hojas de Acacia',
  [BlockId.VINE]: 'Enredadera',
  [BlockId.LILY_PAD]: 'Nenúfar',
  [BlockId.ENCHANTING_TABLE]: 'Mesa de Encantamientos',
  [BlockId.BREWING_STAND]:    'Soporte de Pociones',
  [BlockId.NETHER_PORTAL]:    'Portal del Nether',
  [BlockId.LAVA]:             'Lava',
  [BlockId.NETHER_GOLD_ORE]:  'Oro del Nether',
  [BlockId.NETHER_QUARTZ_ORE]:'Cuarzo del Nether',
  [BlockId.REDSTONE_WIRE]:    'Cable de Redstone',
  [BlockId.LEVER]:            'Palanca',
  [BlockId.BUTTON]:           'Botón',
  [BlockId.PRESSURE_PLATE]:   'Placa de Presión',
  [BlockId.REDSTONE_TORCH]:   'Antorcha de Redstone',
  [BlockId.REPEATER]:         'Repetidor',
  [BlockId.TRAPDOOR]:         'Trampilla',
  [BlockId.STAIRS_STONE]:     'Escaleras de Piedra',
  [BlockId.STAIRS_WOOD]:      'Escaleras de Madera',
  [BlockId.SIGN]:             'Cartel',
  [BlockId.JUKEBOX]:          'Jukebox',
  [BlockId.NOTEBLOCK]:        'Bloque de Notas',
  [BlockId.GRAVE_BASE]:       'Loza de Tumba',
  [BlockId.COFFIN]:           'Ataúd',
  [BlockId.COFFIN_OPEN]:      'Ataúd Abierto',
  [BlockId.CROSS]:            'Cruz',
  [BlockId.END_PORTAL_FRAME]: 'Marco del End Portal',
  [BlockId.END_PORTAL]:       'Portal del End',
  [BlockId.PURPUR_BLOCK]:     'Bloque de Púrpur',
  [BlockId.PURPUR_PILLAR]:    'Pilar de Púrpur',
  [BlockId.END_STONE_BRICKS]: 'Ladrillos del End',
  [BlockId.CHORUS_PLANT]:     'Planta de Coro',
  [BlockId.DRAGON_EGG]:       'Huevo de Dragón',
  [BlockId.END_ROD]:          'Vara del End',
  // Farming & Village blocks (Fase 5)
  [BlockId.CARROT]:           'Zanahoria',
  [BlockId.POTATO]:           'Papa',
  [BlockId.BEETROOT]:         'Remolacha',
  [BlockId.BERRY_BUSH]:       'Arbusto de Bayas',
  [BlockId.PUMPKIN_STEM]:     'Tallo de Calabaza',
  [BlockId.MELON_STEM]:       'Tallo de Melón',
  [BlockId.SUGAR_CANE]:       'Caña de Azúcar',
  [BlockId.BELL]:             'Campana',
  // Redstone automation (Fase 6)
  [BlockId.PISTON]:           'Pistón',
  [BlockId.PISTON_HEAD]:      'Cabeza de Pistón',
  [BlockId.STICKY_PISTON]:    'Pistón Pegajoso',
  [BlockId.HOPPER]:           'Tolva',
  [BlockId.DROPPER]:          'Lanzador',
  [BlockId.DISPENSER]:        'Dispensador',
  [BlockId.OBSERVER]:         'Observador',
  [BlockId.REDSTONE_LAMP]:    'Lámpara de Redstone',
  [BlockId.REDSTONE_LAMP_LIT]:'Lámpara de Redstone',
  [BlockId.DAYLIGHT_SENSOR]:  'Sensor de Luz Solar',
  [BlockId.POWERED_RAIL]:     'Riel Potenciado',
  [BlockId.DETECTOR_RAIL]:    'Riel Detector',
  [BlockId.RAIL]:             'Riel',
  // Exploration blocks (Fase 7)
  [BlockId.DRIPSTONE]:        'Dripstone',
  [BlockId.POINTED_DRIPSTONE]:'Estalactita',
  [BlockId.AZALEA]:           'Azalea',
  [BlockId.AZALEA_LEAVES]:    'Hojas de Azalea',
  [BlockId.GLOW_BERRY_VINE]:  'Enredadera de Bayas',
  [BlockId.SCULK]:            'Sculk',
  [BlockId.SCULK_SENSOR]:     'Sensor de Sculk',
  [BlockId.MOSS_BLOCK]:       'Bloque de Musgo',
  [BlockId.MYCELIUM]:         'Micelio',
  [BlockId.TERRACOTTA]:       'Terracota',
  [BlockId.TERRACOTTA_RED]:   'Terracota Roja',
  [BlockId.TERRACOTTA_ORANGE]:'Terracota Naranja',
  // Decorative blocks (Fase 8)
  [BlockId.WOOL_RED]:          'Lana Roja',
  [BlockId.WOOL_BLUE]:         'Lana Azul',
  [BlockId.WOOL_GREEN]:        'Lana Verde',
  [BlockId.WOOL_YELLOW]:       'Lana Amarilla',
  [BlockId.WOOL_BLACK]:        'Lana Negra',
  [BlockId.WOOL_ORANGE]:       'Lana Naranja',
  [BlockId.WOOL_PURPLE]:       'Lana Morada',
  [BlockId.GLASS_RED]:         'Vidrio Rojo',
  [BlockId.GLASS_BLUE]:        'Vidrio Azul',
  [BlockId.GLASS_GREEN]:       'Vidrio Verde',
  [BlockId.GLASS_YELLOW]:      'Vidrio Amarillo',
  [BlockId.LANTERN]:           'Linterna',
  [BlockId.CHAIN]:             'Cadena',
  [BlockId.CAMPFIRE]:          'Fogata',
  [BlockId.CAMPFIRE_LIT]:      'Fogata Encendida',
  [BlockId.CANDLE]:            'Vela',
  [BlockId.SCAFFOLDING]:       'Andamio',
  [BlockId.BARREL]:            'Barril',
  [BlockId.LOOM]:              'Telar',
  [BlockId.SMOKER]:            'Ahumador',
};

// Render a mini isometric block icon as HTML
function renderBlockIcon(blockId, size) {
  const side = BLOCK_COLORS[blockId] || '#888';
  const top = BLOCK_TOP_COLORS[blockId] || side;
  const s = size || 28;
  const h = s * 0.5;
  // Simple isometric look using CSS
  return `
    <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
      <div style="position:absolute;bottom:0;left:1px;width:${s-6}px;height:${s-6}px;background:${side};
        border:2px solid rgba(0,0,0,0.25);
        box-shadow:inset ${Math.floor(s/8)}px ${Math.floor(s/8)}px 0 rgba(255,255,255,0.2),
                   inset -${Math.floor(s/12)}px -${Math.floor(s/12)}px 0 rgba(0,0,0,0.15);
      "></div>
      <div style="position:absolute;top:0;left:1px;width:${s-6}px;height:${Math.floor(h*0.4)}px;background:${top};
        border-left:2px solid rgba(0,0,0,0.15);border-right:2px solid rgba(0,0,0,0.15);border-top:2px solid rgba(0,0,0,0.15);
      "></div>
    </div>
  `;
}

// Render a non-block item icon (diamond shape for items, not cube)
function renderItemIcon(itemId, size) {
  const color = ITEM_COLORS_MAP[itemId] || '#888';
  const s = size || 28;
  // Render as a rounded colored square with a slight diamond/gem look
  return `
    <div style="width:${s}px;height:${s}px;position:relative;image-rendering:pixelated;">
      <div style="position:absolute;top:50%;left:50%;width:${s-10}px;height:${s-10}px;
        transform:translate(-50%,-50%) rotate(0deg);
        background:${color};
        border:2px solid rgba(0,0,0,0.3);
        border-radius:3px;
        box-shadow:inset 2px 2px 0 rgba(255,255,255,0.3),
                   inset -2px -2px 0 rgba(0,0,0,0.2);
      "></div>
    </div>
  `;
}

export { BLOCK_COLORS, BLOCK_TOP_COLORS, BLOCK_NAMES, ITEM_COLORS_MAP, ITEM_NAMES_MAP, renderBlockIcon, renderItemIcon };

export class Hotbar {
  constructor(inventory) {
    this.inventory = inventory;

    // Inject durability pulse animation
    if (!document.getElementById('hb-durability-style')) {
      const style = document.createElement('style');
      style.id = 'hb-durability-style';
      style.textContent = '@keyframes hb-pulse{from{opacity:1}to{opacity:0.4}}';
      document.head.appendChild(style);
    }

    // Minecraft-style hotbar container
    this.container = document.createElement('div');
    this.container.id = 'hotbar';
    this.container.style.cssText = `
      position: fixed;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 0px;
      pointer-events: none;
      z-index: 100;
      padding: 2px;
      background: rgba(0,0,0,0.45);
      border: 3px solid rgba(20,20,20,0.8);
      border-radius: 2px;
      image-rendering: pixelated;
    `;

    this.slotElements = [];
    for (let i = 0; i < 9; i++) {
      const slot = document.createElement('div');
      slot.style.cssText = `
        width: 44px;
        height: 44px;
        border: 2px solid rgba(40,40,40,0.9);
        background: rgba(80,80,80,0.35);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        font-family: 'Courier New', monospace;
        color: white;
        margin: 1px;
        image-rendering: pixelated;
      `;
      this.slotElements.push(slot);
      this.container.appendChild(slot);
    }

    // Block name tooltip (Minecraft style - appears above hotbar)
    this.tooltip = document.createElement('div');
    this.tooltip.style.cssText = `
      position: fixed;
      bottom: 60px;
      left: 50%;
      transform: translateX(-50%);
      color: white;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      pointer-events: none;
      z-index: 100;
      text-shadow: 2px 2px 0 #3F3F3F, -1px -1px 0 #3F3F3F, 1px -1px 0 #3F3F3F, -1px 1px 0 #3F3F3F;
      letter-spacing: 1px;
    `;
    document.body.appendChild(this.tooltip);
    document.body.appendChild(this.container);

    this._lastSelected = -1;
    this._tooltipTimer = 0;
  }

  update() {
    const now = Date.now();

    // Show tooltip briefly when selection changes
    if (this.inventory.selectedIndex !== this._lastSelected) {
      this._lastSelected = this.inventory.selectedIndex;
      this._tooltipTimer = now + 2000;
    }

    for (let i = 0; i < 9; i++) {
      const el = this.slotElements[i];
      const item = this.inventory.slots[i];
      const selected = i === this.inventory.selectedIndex;

      /* El borde del hueco toma el color de la rareza: es la forma más barata
         de que Fede vea de un vistazo qué lleva bueno encima, sin abrir nada.
         El seleccionado manda por encima, para no perder de vista cuál es. */
      const colorRareza = item && item.rareza && item.rareza !== 'comun' ? colorDe(item) : null;

      // Minecraft: selected slot has bright white/yellow border
      if (selected) {
        el.style.border = '2px solid rgba(220,220,220,0.95)';
        el.style.background = 'rgba(255,255,255,0.12)';
        el.style.boxShadow = 'inset 0 0 8px rgba(255,255,255,0.1)';
      } else if (colorRareza) {
        el.style.border = `2px solid ${colorRareza}`;
        el.style.background = 'rgba(80,80,80,0.35)';
        el.style.boxShadow = `inset 0 0 10px ${colorRareza}55`;
      } else {
        el.style.border = '2px solid rgba(40,40,40,0.9)';
        el.style.background = 'rgba(80,80,80,0.35)';
        el.style.boxShadow = 'none';
      }

      if (item && item.count > 0) {
        // Determine which icon renderer to use
        let iconHtml;
        if (item.itemId !== undefined) {
          // Non-block item (negative ID)
          iconHtml = renderItemIcon(item.itemId, 30);
        } else {
          // Block item (positive ID)
          iconHtml = renderBlockIcon(item.blockId, 30);
        }
        // Durability bar for tools
        let durabilityHtml = '';
        if (isTool(item)) {
          const pct = getToolDurabilityPercent(item);
          if (pct < 1) {
            const color = getToolDurabilityColor(item);
            const width = Math.max(1, Math.round(pct * 100));
            const pulse = pct <= 0.1 ? 'animation:hb-pulse 0.5s infinite alternate;' : '';
            durabilityHtml = `<div style="position:absolute;bottom:2px;left:4px;right:4px;height:2px;background:#333;border-radius:1px;">
              <div style="height:100%;width:${width}%;background:${color};border-radius:1px;${pulse}"></div></div>`;
          }
        }

        el.innerHTML = `
          ${iconHtml}
          ${durabilityHtml}
          <span style="font-size:11px;font-weight:bold;position:absolute;bottom:${durabilityHtml ? '5' : '1'}px;right:3px;
            text-shadow:2px 2px 0 #3F3F3F, -1px 0 0 #3F3F3F, 0 -1px 0 #3F3F3F;
            color:white;font-family:'Courier New',monospace;">${item.count > 1 ? item.count : ''}</span>
        `;
      } else {
        el.innerHTML = '';
      }
    }

    // Tooltip
    const selectedItem = this.inventory.getSelectedSlot();
    if (now < this._tooltipTimer && selectedItem && selectedItem.count > 0) {
      let name;
      if (selectedItem.itemId !== undefined) {
        name = ITEM_NAMES_MAP[selectedItem.itemId] || 'Objeto';
      } else {
        name = BLOCK_NAMES[selectedItem.blockId] || 'Unknown';
      }
      this.tooltip.textContent = name;
      this.tooltip.style.opacity = '1';
    } else {
      this.tooltip.style.opacity = '0';
    }
  }
}
